import axios from "axios";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Plus, X, Receipt, CreditCard, PencilSimple, Trash } from "@phosphor-icons/react";
import MenuGrid from "@/components/MenuGrid";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  pending: "bg-blue-100 text-blue-700",
  in_progress: "bg-orange-100 text-orange-700",
  ready: "bg-green-100 text-green-700",
  served: "bg-gray-100 text-gray-600",
  paid: "bg-emerald-100 text-emerald-700",
  cancelled: "bg-red-100 text-red-700",
  refunded: "bg-purple-100 text-purple-700",
  partial_refund: "bg-purple-100 text-purple-700",
};
const TERMINAL_STATUSES = ["paid", "refunded", "partial_refund", "cancelled"];

/**
 * Orders view container: sub-tabs (New / Open / Online / Closed) +
 * either the menu grid (New Order), the order detail card (selected),
 * or the list. Extracted from POSPage.jsx (~175 lines inline).
 */
const OrdersView = ({
  serverMobile,
  ordersSubView, setOrdersSubView,
  fetchOrdersList,
  setTicketOpen,
  menuItems,
  handleMenuItemClick,
  selectedOrder, setSelectedOrder,
  paymentInfo, setPaymentInfo,
  taxRate,
  ordersList,
  tables,
  setCurrentOrder,
  setOrderItems,
  setSelectedTable,
  setIsQuickOrder,
  setTicketName,
  setShowPayment,
  setShowRefund,
  setRefundPin, setRefundReason, setRefundAmount,
  setManagerAction, setManagerPin, setManagerReason,
}) => {
  const openOrderForPayment = (order) => {
    if (!TERMINAL_STATUSES.includes(order.status)) {
      setCurrentOrder(order);
      setOrderItems(order.items || []);
      if (order.table_id) {
        const t = tables.find((tb) => tb.id === order.table_id);
        if (t) setSelectedTable(t);
      } else {
        setSelectedTable(null);
        setIsQuickOrder(true);
      }
      if (order.customer_name) setTicketName(order.customer_name);
      setOrdersSubView("menu");
      setTicketOpen(true);
      setShowPayment(true);
    } else {
      setSelectedOrder(order);
      setPaymentInfo(null);
      axios.get(`${API}/orders/${order.id}/payment-info`)
        .then((r) => setPaymentInfo(r.data))
        .catch(() => {});
    }
  };

  return (
    <div data-testid="pos-orders-view">
      {/* Sub-tabs */}
      <div className={`flex gap-2 mb-4 overflow-x-auto ${serverMobile ? "pb-1" : ""}`}>
        <Button variant={ordersSubView === "menu" ? "default" : "outline"} size="sm" className="gap-2 shrink-0" onClick={() => { setOrdersSubView("menu"); if (!serverMobile) setTicketOpen(true); }} data-testid="orders-new-btn">
          <Plus size={16} /> New Order
        </Button>
        <Button variant={ordersSubView === "open" ? "default" : "outline"} size="sm" className="gap-2 shrink-0" onClick={() => { setOrdersSubView("open"); fetchOrdersList("open"); setTicketOpen(false); }} data-testid="orders-open-btn">
          Open Orders
        </Button>
        <Button variant={ordersSubView === "online" ? "default" : "outline"} size="sm" className="gap-2 shrink-0" onClick={() => { setOrdersSubView("online"); fetchOrdersList("online"); setTicketOpen(false); }} data-testid="orders-online-btn">
          Online Orders
        </Button>
        <Button variant={ordersSubView === "closed" ? "default" : "outline"} size="sm" className="gap-2 shrink-0" onClick={() => { setOrdersSubView("closed"); fetchOrdersList("closed"); setTicketOpen(false); }} data-testid="orders-closed-btn">
          Closed Orders
        </Button>
      </div>

      {ordersSubView === "menu" ? (
        <MenuGrid items={menuItems} onItemClick={handleMenuItemClick} compact={serverMobile} />
      ) : selectedOrder ? (
        <div className={serverMobile ? "" : "max-w-lg"} data-testid="order-detail">
          <Button variant="ghost" size="sm" className="gap-1 mb-3 text-muted-foreground" onClick={() => setSelectedOrder(null)} data-testid="back-to-orders">
            <X size={16} /> Back to list
          </Button>
          <div className="bg-white rounded-xl border border-border p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-heading text-lg font-medium">{selectedOrder.table_name || "Quick Order"}</h3>
                <p className="text-xs text-muted-foreground font-mono">#{selectedOrder.id?.slice(-8)}</p>
              </div>
              <Badge className={`text-xs border-0 ${selectedOrder.status === "refunded" ? "bg-purple-100 text-purple-700" : selectedOrder.status === "paid" ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-600"}`}>
                {selectedOrder.status?.replace("_", " ")}
              </Badge>
            </div>
            <div className="text-sm text-muted-foreground mb-4">
              {selectedOrder.created_at && <p>Date: {new Date(selectedOrder.created_at).toLocaleString()}</p>}
              {paymentInfo && paymentInfo.payment_method !== "unknown" && (
                <p className="mt-1 flex items-center gap-1.5">
                  Paid by: <Badge variant="outline" className="capitalize font-medium">{paymentInfo.payment_method === "card" ? "Credit Card" : paymentInfo.payment_method === "cash" ? "Cash" : paymentInfo.payment_method}</Badge>
                  {paymentInfo.transaction_id && <span className="font-mono text-xs text-muted-foreground">({paymentInfo.transaction_id})</span>}
                </p>
              )}
              {paymentInfo?.tip_amount > 0 && <p className="mt-1">Tip: <span className="font-mono font-medium">${paymentInfo.tip_amount.toFixed(2)}</span></p>}
              {selectedOrder.refunded_by && <p className="text-purple-600 mt-1">Refunded by: {selectedOrder.refunded_by}</p>}
              {selectedOrder.refund_reason && <p className="text-purple-600">Reason: {selectedOrder.refund_reason}</p>}
              {selectedOrder.refund_amount && <p className="text-purple-600">Refund amount: <span className="font-mono">${selectedOrder.refund_amount.toFixed(2)}</span></p>}
            </div>
            <Separator className="mb-3" />
            <div className="space-y-2 mb-4">
              {(selectedOrder.items || []).map((item, idx) => (
                <div key={idx} className="flex items-start justify-between py-1.5 border-b border-dashed border-border/50 last:border-0">
                  <div>
                    <p className="text-sm"><span className="font-mono text-muted-foreground mr-2">{item.quantity}x</span>{item.menu_item_name}</p>
                    {item.modifiers?.length > 0 && <p className="text-xs text-muted-foreground ml-6">{item.modifiers.map((m) => m.name).join(", ")}</p>}
                  </div>
                  <span className="font-mono text-sm shrink-0">${(item.total_price || 0).toFixed(2)}</span>
                </div>
              ))}
            </div>
            <Separator className="mb-3" />
            <div className="space-y-1.5">
              <div className="flex justify-between text-sm"><span className="text-muted-foreground">Subtotal</span><span className="font-mono">${((selectedOrder.total || 0) / (1 + taxRate)).toFixed(2)}</span></div>
              <div className="flex justify-between text-sm"><span className="text-muted-foreground">Tax ({(taxRate * 100).toFixed(1)}%)</span><span className="font-mono">${((selectedOrder.total || 0) - (selectedOrder.total || 0) / (1 + taxRate)).toFixed(2)}</span></div>
              {selectedOrder.tip_amount > 0 && <div className="flex justify-between text-sm"><span className="text-muted-foreground">Tip</span><span className="font-mono">${selectedOrder.tip_amount.toFixed(2)}</span></div>}
              <Separator />
              <div className="flex justify-between font-medium text-lg"><span>Total</span><span className="font-mono">${(selectedOrder.total || 0).toFixed(2)}</span></div>
            </div>
            {selectedOrder.status === "paid" && (
              <Button variant="destructive" className="w-full mt-4 gap-2 h-12" onClick={() => { setShowRefund(true); setRefundPin(""); setRefundReason(""); setRefundAmount(String((selectedOrder.total || 0).toFixed(2))); }} data-testid="refund-btn">
                <CreditCard size={18} /> Issue Refund
              </Button>
            )}
            {selectedOrder.status === "refunded" && (
              <div className="mt-4 bg-purple-50 border border-purple-200 rounded-lg p-3 text-center text-sm text-purple-700">
                This order has been refunded
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-2" data-testid="orders-list">
          {ordersList.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">
              <Receipt size={48} className="mx-auto mb-3 opacity-30" />
              <p className="font-medium">No {ordersSubView} orders</p>
            </div>
          ) : (
            ordersList.map((order) => (
              <div
                key={order.id}
                className="bg-white rounded-xl border border-border p-4 flex items-center gap-4 hover:shadow-sm transition-shadow cursor-pointer"
                onClick={() => openOrderForPayment(order)}
                data-testid={`order-${order.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="font-medium text-sm">{order.table_name || "Quick Order"}</p>
                    <Badge className={`text-xs border-0 ${STATUS_COLORS[order.status] || "bg-gray-100 text-gray-600"}`}>
                      {order.status?.replace("_", " ")}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground truncate">
                    {(order.items || []).map((i) => `${i.quantity}x ${i.menu_item_name}`).join(", ")}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {order.created_at ? new Date(order.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""}
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <p className="font-mono font-medium">${(order.total || 0).toFixed(2)}</p>
                  <p className="text-xs text-muted-foreground">{(order.items || []).length} items</p>
                </div>
                {!TERMINAL_STATUSES.includes(order.status) && (
                  <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-muted-foreground hover:text-primary hover:bg-primary/10"
                      onClick={(e) => { e.stopPropagation(); setManagerAction({ orderId: order.id, mode: "edit", order }); setManagerPin(""); setManagerReason(""); }}
                      title="Modify order (manager override)"
                      data-testid={`order-edit-${order.id}`}
                    >
                      <PencilSimple size={16} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                      onClick={(e) => { e.stopPropagation(); setManagerAction({ orderId: order.id, mode: "delete", order }); setManagerPin(""); setManagerReason(""); }}
                      title="Delete order (manager override)"
                      data-testid={`order-delete-${order.id}`}
                    >
                      <Trash size={16} />
                    </Button>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default OrdersView;
